import { useMemo } from 'react';
import { DisplayGame } from '../data/types';
import { mlbTeams } from '../data/mlbTeams';

export interface LineupPlayer {
  name: string;
  playerId: string;
  avatar: string;
  position: string;
  team: string;
  battingOrder: number; // 0 = pitcher, 1-9 = batting order
  confirmedLineup?: boolean | null;
  hand?: string; // "L", "R", or "S"
  injuryStatus?: string;
  injuryNotes?: string;
}

export interface TeamLineup {
  name: string;
  abbr: string;
  logo: string;
  confirmed: boolean; // true = we have the confirmed lineup, false = not yet available
  startingPitcher?: LineupPlayer;
  batters: LineupPlayer[]; // Sorted by batting order 1-9
}

export interface GameLineupReport {
  gameId: string;
  startTime: string;
  gameTime: string;
  awayTeam: TeamLineup;
  homeTeam: TeamLineup;
}

export interface LineupReportData {
  date: string;
  games: GameLineupReport[];
  confirmedCount: number;
  pendingCount: number;
}

function buildTeamLineup(
  teamAbbr: string,
  teamName: string,
  teamLogo: string,
  players: DisplayGame['players'],
): TeamLineup {
  const teamPlayers = players.filter(
    (p) => p.team.toUpperCase() === teamAbbr.toUpperCase()
  );

  // Separate pitcher (battingOrder === 0) from batters
  const lineupPlayers = teamPlayers
    .filter((p) => p.battingOrder !== undefined)
    .map((p) => ({
      name: p.name,
      playerId: p.id,
      avatar: p.avatar,
      position: p.position,
      team: p.team,
      battingOrder: p.battingOrder!,
      confirmedLineup: p.confirmedLineup,
      hand: p.hand,
      injuryStatus: p.injuryStatus,
      injuryNotes: p.injuryNotes,
    }));

  const startingPitcher = lineupPlayers.find((p) => p.battingOrder === 0);

  const batters = lineupPlayers
    .filter((p) => p.battingOrder > 0)
    .sort((a, b) => a.battingOrder - b.battingOrder);

  // Team is confirmed only when batters are confirmed (pitcher alone doesn't count)
  const isConfirmed = batters.some((b) => b.confirmedLineup === true);

  return {
    name: teamName,
    abbr: teamAbbr,
    logo: teamLogo || mlbTeams[teamAbbr.toUpperCase()]?.logo || '',
    confirmed: isConfirmed,
    startingPitcher,
    batters,
  };
}

function transformGamesToLineupReport(games: DisplayGame[]): LineupReportData {
  const gameReports: GameLineupReport[] = games.map((game) => {
    const awayTeam = buildTeamLineup(
      game.awayTeam.abbr,
      game.awayTeam.name,
      game.awayTeam.logo,
      game.players,
    );
    const homeTeam = buildTeamLineup(
      game.homeTeam.abbr,
      game.homeTeam.name,
      game.homeTeam.logo,
      game.players,
    );

    return {
      gameId: game.id,
      startTime: game.startTime,
      gameTime: game.time,
      awayTeam,
      homeTeam,
    };
  });

  // Sort games by start time
  gameReports.sort((a, b) => {
    const timeA = a.startTime ? new Date(a.startTime).getTime() : Infinity;
    const timeB = b.startTime ? new Date(b.startTime).getTime() : Infinity;
    return timeA - timeB;
  });

  // Count teams, not games
  let confirmedCount = 0;
  let pendingCount = 0;
  for (const g of gameReports) {
    confirmedCount += (g.awayTeam.confirmed ? 1 : 0) + (g.homeTeam.confirmed ? 1 : 0);
    pendingCount += (g.awayTeam.confirmed ? 0 : 1) + (g.homeTeam.confirmed ? 0 : 1);
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  return {
    date: dateStr,
    games: gameReports,
    confirmedCount,
    pendingCount,
  };
}

export function useLineupReport(games: DisplayGame[], league: string) {
  const data = useMemo(() => {
    if (!games || games.length === 0 || league !== 'MLB') {
      return null;
    }
    return transformGamesToLineupReport(games);
  }, [games, league]);

  return { data };
}

export default useLineupReport;
